import { Component, Input, OnInit, inject } from "@angular/core";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { VisitStore } from "@app/services/store/visit-store";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { BookingNavigationService } from "@app/services/navigation/booking-navigation.service";
import { Visit, Visit_DetailProjection } from "@app/models/rest/visit";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import {
  ColumnConfig,
  DateColumnConfig,
  FilterConfig,
  RowExpanderConfig,
  WidgetColumnConfig
} from "@bryntum/grid-thin";
import {
  CollectionFilterConfig,
  FieldFilterPickerConfig,
  Model,
  Store as BryntumStore
} from "@bryntum/core-thin";
import { PriorityStore } from "@app/services/store/priority-store";
import { VisitTypeStore } from "@app/services/store/visit-type-store";
import { GridComponent } from "../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { AvailabilityVisitService } from "@app/services/http/availability-visit.service";
import { BulkVisitTransformDialogComponent } from "@app/view/shared/bulk-visit-transform/bulk-visit-transform-dialog.component";
import { BulkVisitTransformRequest } from "@app/models/dto/bulk-visit-transform-request";
import { ConfirmDialogComponent } from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { VisitStatus } from "@app/models/enums/visit-status.enum";

// TODO: Move to shared module

@Component({
  selector: "app-visit-table-viewer",
  templateUrl: "./visit-table-viewer.component.html",
  styleUrls: ["./visit-table-viewer.component.scss"],
  imports: [GridComponent, MatPaginator]
})
export class VisitTableViewerComponent
  extends BaseDataTablePageDirective<Visit>
  implements OnInit
{
  protected visitStore: VisitStore;
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  protected priorityStore = inject(PriorityStore);
  protected visitTypeStore = inject(VisitTypeStore);
  private bookingNavigationService = inject(BookingNavigationService);
  private availabilityVisitService = inject(AvailabilityVisitService);

  /**
   * When true, the grid renders a checkbox column and tracks multiple selected rows so the user
   * can issue bulk actions. Off by default so single-row interactions stay unchanged.
   */
  public bulkMode = false;
  /** Selected records mirrored from the grid's `selectionchange` event. */
  public selectedVisits: Visit[] = [];

  /**
   * Bryntum selection config — checkbox column appears only while bulkMode is enabled.
   *
   * Note: Bryntum merges (assigns) new selectionMode objects into the existing proxy rather than
   * replacing it. Keys absent from the new object keep their prior value, so we must explicitly
   * set `checkbox: false` and `showCheckAll: false` when disabling bulk mode; otherwise the
   * selection column lingers after toggling off.
   */
  public get selectionModeConfig(): object {
    return this.bulkMode
      ? { row: true, multiSelect: true, checkbox: true, showCheckAll: true }
      : { row: true, multiSelect: false, checkbox: false, showCheckAll: false };
  }

  @Input()
  public filter: object[] = [];

  public rowExpanderConfig: Partial<RowExpanderConfig> = {
    columnPosition: "last",
    renderer: async ({ record }) => {
      console.log("Rendering visit card for record", record);
      const { id } = record;
      console.log("Extracted ID", id);
      return `<visit-card-component visit-id="${id}" style="width:100%"></visit-card-component>`;
    }
  };

  @Input()
  public columns: (ColumnConfig | DateColumnConfig | WidgetColumnConfig)[] = [
    {
      field: "id",
      text: "ID",
      flex: 1,
      editor: false
    },
    {
      field: "job.externalJobReference",
      text: "Job",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "job.externalPropertyReference",
      text: "Property ID",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "visitStatus",
      text: "Status",
      flex: 1,
      editor: false
    },
    {
      field: "visitType.description",
      text: "Type",
      filterable: true,
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "job.postCode",
      text: "Post Code",
      filterable: true,
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "appointmentStart",
      text: "Appointment Start",
      flex: 1,
      format: "DD/MM/YYYY HH:mm",
      type: "date",
      editor: false
    },
    {
      field: "appointmentEnd",
      text: "Appointment End",
      flex: 1,
      format: "DD/MM/YYYY HH:mm",
      type: "date",
      editor: false
    },
    {
      field: "job.priority.description",
      text: "Priority",
      flex: 1,
      editor: false,
      sortable: true
    },
    {
      field: "autoBookProfileOverride.description",
      text: "Auto Book Override",
      flex: 1,
      editor: false,
      hidden: true,
      sortable: false
    },
    {
      type: "jobJump",
      text: "Job Link",
      exportable: false
    } as unknown as ColumnConfig
  ];

  fakeVisitStore = new BryntumStore({
    fields: ["id", "name", "status"],
    data: [
      {
        id: 1,
        externalJobReference: "Job 1",
        externalProjectReference: "sample",
        visitStatus: "NEW",
        postCode: "BN1 1AA"
      },
      { id: 2, name: "Job 2", visitStatus: "REFERRED" },
      { id: 3, name: "Job 3", visitStatus: "BOOKED" },
      { id: 4, name: "Job 4", visitStatus: "IN_PROGRESS" },
      { id: 5, name: "Job 6", visitStatus: "CANCELLED" },
      { id: 6, name: "Job 7", visitStatus: "COMPLETED" }
    ]
  });

  // statusFilterCreated: boolean = false;
  filterConfig: FilterConfig = {
    pickerConfig: {
      showAddFilterButton: false,
      dateFormat: "DD/MM/YYYY",

      canDeleteFilter: (filter) => {
        return filter.property !== "status";
      },
      getFieldFilterPickerConfig: (
        collectionFilterConfig: CollectionFilterConfig
      ): Partial<FieldFilterPickerConfig> => {
        switch (collectionFilterConfig.property) {
          case "appointmentStart":
          case "appointmentEnd":
            return {
              dateFormat: "DD/MM/YYYY",
              getValueFieldConfig: (filter, fieldConfig) => {
                console.log(filter, fieldConfig);
                fieldConfig.type = "date";
                fieldConfig.format = "DD/MM/YYYY";
                return fieldConfig;
              },
              operators: this.dateOperators
            };
          case "job.externalPropertyReference":
          case "job.externalProjectReference":
          case "job.externalCaseReference":
          case "job.externalJobReference":
          case "job.postCode":
            let stringFieldResult: any = {
              store: this.fakeVisitStore,
              operators: this.stringOperators
            };
            if (
              this.gridStore.filters.findItem(
                "property",
                collectionFilterConfig.property
              ) === null
            ) {
              stringFieldResult.filter = {
                id: `${collectionFilterConfig.property}-isIncludedIn`,
                property: collectionFilterConfig.property,
                operator:
                  collectionFilterConfig.operator === "includes"
                    ? "*"
                    : collectionFilterConfig.operator,
                value: collectionFilterConfig.value
              };
            }
            return stringFieldResult;
          case "job.priority.description":
          case "visitType.description":
          case "visitStatus":
            const fixedFieldResult: any = {
              operatorLocked: true,
              propertyLocked: true,
              store: this.fakeVisitStore,
              operators: this.fixedSetOperators,
              getValueFieldConfig: (filter, fieldConfig) => {
                fieldConfig.createOnUnmatched = false;
                return fieldConfig;
              }
            };
            if (
              this.gridStore.filters.findItem(
                "property",
                collectionFilterConfig.property
              ) === null
            ) {
              fixedFieldResult.filter = {
                id: `${collectionFilterConfig.property}-isIncludedIn`,
                property: collectionFilterConfig.property,
                operator: "isIncludedIn",
                value: collectionFilterConfig.value,
                disabled: collectionFilterConfig.disabled
              };
            }
            return fixedFieldResult;
        }
        return {};
      }
    }
  };

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const visitStore = inject(VisitStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      visitStore,
      store,
      dialog,
      "Visit",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );

    this.visitStore = visitStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  ngOnInit() {
    super.ngOnInit();

    this.priorityStore.loadAll().then((value) => {
      value.resources.forEach((priority, index) => {
        this.fakeVisitStore.add({
          id: index + 2000,
          name: priority.description,
          job: {
            priority: priority
          }
        });
      });
    });

    this.visitTypeStore.loadAll().then((value) => {
      value.resources.forEach((visitType, index) => {
        this.fakeVisitStore.add({
          id: index + 3000,
          name: visitType.description,
          visitType: visitType
        });
      });
    });
  }

  gridTotalChange($event: any) {
    console.log("total change");
    console.log($event);
  }

  openJob(visit: Visit_DetailProjection) {
    this.router.navigate(["/jobs", "detail", visit.job.id]).then();
  }

  visitBook(record: Visit) {
    console.log("firing visit dialog", record);
    // Store the current route so booking components can return here
    this.bookingNavigationService.setOriginalRoute("/data-view/visits");
    this.router.navigate(["/booking/visit/", record.id]).then();
  }

  /** Mirrors the grid's selectionchange event into `selectedVisits`. */
  override onEvent($event: any) {
    super.onEvent($event);
    if ($event.type === "selectionchange") {
      const records: Model[] = $event.selection || [];
      this.selectedVisits = records.map((r: any) => r.data ?? r) as Visit[];
    }
  }

  toggleBulkMode(enabled: boolean) {
    this.bulkMode = enabled;
    if (!enabled) {
      this.selectedVisits = [];
      this.grid?.gridEngine?.deselectAll();
    }
  }

  openBulkTransformDialog() {
    if (this.selectedVisits.length === 0) {
      return;
    }
    const dialogRef = this.dialog.open(BulkVisitTransformDialogComponent, {
      data: {
        visits: this.selectedVisits.map((v) => ({
          id: v.id,
          visitStatus: v.visitStatus as VisitStatus
        }))
      },
      width: "520px"
    });
    dialogRef
      .afterClosed()
      .subscribe((request: BulkVisitTransformRequest | undefined) => {
        if (!request) {
          return;
        }
        this.applyBulkTransform(request);
      });
  }

  private applyBulkTransform(request: BulkVisitTransformRequest) {
    this.ngxUiLoaderService.start();
    this.availabilityVisitService
      .bulkVisitTransform(request)
      .then((response) => {
        this.ngxUiLoaderService.stop();
        const transformed = response.transformedVisitIds?.length ?? 0;
        const skipped = response.skippedVisits?.length ?? 0;
        const messages = (response.skippedVisits ?? []).map(
          (s) => `Visit ${s.visitId}: ${s.reason}`
        );
        this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: "Bulk transform complete",
            message: `${transformed} visit(s) transformed, ${skipped} skipped.`,
            messages,
            buttonMode: "ok-cancel",
            showCancelButton: false
          }
        });
        this.selectedVisits = [];
        this.grid?.gridEngine?.deselectAll();
        this.gridStore.load({}).then();
      })
      .catch((error) => {
        this.ngxUiLoaderService.stop();
        this.serverErrorHandler.handleError(error);
      });
  }
}
